import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Building2, CheckCircle, ExternalLink, GraduationCap } from 'lucide-react';
import { useSiteLanguage } from '../context/SiteLanguageContext';
import Seo from '../components/Seo';

const OTHM_UNIVERSITIES = [
  'Anglia Ruskin University',
  'Arden University',
  'Aston University',
  'Bangor University',
  'Buckinghamshire New University',
  'Cardiff Metropolitan University',
  'Coventry University',
  'Curtin University',
  'De Montfort University',
  'Dublin Business School',
  'Edith Cowan University, Australia',
  'GlobalNxt University, Malaysia',
  'London Graduate School (Malta)',
  'Northumbria University',
  'Queen Margaret University Edinburgh',
  'Sheffield Hallam University',
  'Swiss Education Group',
  'Teesside University',
  'Ulster University',
  'University College Birmingham',
  'University College Dublin, Singapore',
  'University of Chester',
  'University of Gloucestershire',
  'University of Greater Manchester',
  'University of Greenwich',
  'University of Hertfordshire',
  'University of Law',
  'University of Northampton',
  'University of South Wales',
  'University of Staffordshire',
  'University of Sunderland',
  'University of Sunderland in Hong Kong',
  'University of the Highlands and Islands',
  'University of the West of England (UWE Bristol)',
  'University of Westminster',
];

const QUALIFI_UNIVERSITIES = [
  'Anglia Ruskin University',
  'Arden University',
  'Bangor University',
  'Bath Spa University',
  'Birmingham City University',
  'BPP University',
  'Buckinghamshire New University',
  'De Montfort University',
  'Glasgow Caledonian University',
  'Leeds Beckett University',
  'Liverpool John Moores University',
  'London South Bank University',
  'Northumbria University',
  'Plymouth Marjon University',
  'Staffordshire University',
  'The Open University',
  'University of Bedfordshire',
  'University of Bolton',
  'University of Central Lancashire',
  'University of Chichester',
  'University of Cumbria',
  'University of Derby',
  'University of East London',
  'University of Gloucestershire',
  'University of Huddersfield',
  'University of Law',
  'University of Leicester',
  'University of Lincoln',
  'University of New England',
  'University of Suffolk',
  'University of the West of Scotland',
  'University of Wolverhampton',
  'Wrexham University',
];

const CONTENT = {
  en: {
    heroEyebrow: 'Pathways to Higher Study',
    heroTitle: 'University Progression',
    heroDescription: 'Explore how OTHM and QUALIFI qualifications can support progression into bachelor\'s top-up degrees, master\'s programmes and MBA routes in the UK and internationally.',
    noticeTitle: 'Important entry note',
    noticeBody: 'University admission remains subject to the institution\'s own assessment, English-language expectations and programme-specific requirements.',
    stepsEyebrow: 'How It Works',
    stepsTitle: 'How progression usually works',
    steps: [
      'Complete the relevant diploma or qualification level.',
      'Match your qualification to a university top-up or postgraduate route.',
      'Meet any additional admissions, English-language or professional requirements set by the university.',
    ],
    othmEyebrow: 'OTHM Qualifications',
    othmTitle: 'OTHM progression routes',
    othmSummary1: 'OTHM states that completing its qualifications can meet standard academic entry requirements, with each applicant still assessed individually by the university.',
    othmSummary2: 'Its published progression routes highlight top-up degrees, master\'s programmes and MBA pathways across universities in the UK and overseas.',
    qualifiEyebrow: 'QUALIFI Qualifications',
    qualifiTitle: 'QUALIFI progression routes',
    qualifiSummary1: 'Qualifi states that its learners progress into degree programmes through advanced entry or direct admission across the UK and internationally.',
    qualifiSummary2: 'Its published progression page highlights universities where Qualifi graduates have moved into undergraduate and postgraduate study routes.',
    bullet1: 'Advanced standing may be available for Levels 4 to 7 depending on the route.',
    bullet2: 'Progression can include bachelor\'s top-up, master\'s and MBA options.',
    bullet3: 'Published university examples come from the awarding bodies\' own progression pages.',
    bullet4: 'Applicants should confirm current availability and entry criteria before applying.',
    universitiesLabel: 'Examples of universities listed on the published progression page',
    bodyPage: 'Awarding body page',
    officialPage: 'Official progression page',
    compareTitle: 'Use this page as a starting point',
    compareBody: 'We have summarised the published OTHM and Qualifi progression information here so learners can quickly see common university pathways before speaking to an adviser.',
    browseCourses: 'Browse courses',
    speakAdviser: 'Speak to an adviser',
  },
  ar: {
    heroEyebrow: 'مسارات إلى الدراسة الجامعية',
    heroTitle: 'التقدم إلى الجامعة',
    heroDescription: 'اكتشف كيف يمكن لمؤهلات OTHM و QUALIFI أن تدعم انتقالك إلى برامج البكالوريوس التكميلية والماجستير وMBA في المملكة المتحدة وعلى المستوى الدولي.',
    noticeTitle: 'ملاحظة مهمة حول القبول',
    noticeBody: 'يبقى القبول الجامعي خاضعاً لتقييم الجامعة نفسها ومتطلبات اللغة الإنجليزية وشروط البرنامج المحددة.',
    stepsEyebrow: 'كيف يعمل ذلك',
    stepsTitle: 'كيف يتم التقدم عادةً',
    steps: [
      'أكمل الدبلوم أو المستوى الدراسي المناسب.',
      'طابق مؤهلك مع برنامج جامعي تكميلي أو مسار دراسات عليا.',
      'استوفِ أي متطلبات إضافية للقبول أو اللغة أو المهنة تحددها الجامعة.',
    ],
    othmEyebrow: 'مؤهلات OTHM',
    othmTitle: 'مسارات التقدم الخاصة بـ OTHM',
    othmSummary1: 'توضح OTHM أن إكمال مؤهلاتها قد يلبّي متطلبات القبول الأكاديمية القياسية، مع بقاء التقييم النهائي لكل متقدم بيد الجامعة.',
    othmSummary2: 'وتبرز مساراتها المنشورة فرص الانتقال إلى برامج البكالوريوس التكميلية والماجستير وMBA في جامعات داخل المملكة المتحدة وخارجها.',
    qualifiEyebrow: 'مؤهلات QUALIFI',
    qualifiTitle: 'مسارات التقدم الخاصة بـ QUALIFI',
    qualifiSummary1: 'تشير Qualifi إلى أن متعلميها ينتقلون إلى البرامج الجامعية من خلال القبول المباشر أو الدخول المتقدم في المملكة المتحدة ودول أخرى.',
    qualifiSummary2: 'وتعرض صفحتها المنشورة جامعات انتقل إليها خريجو Qualifi إلى مسارات دراسية جامعية ودراسات عليا.',
    bullet1: 'قد يتاح احتساب أكاديمي متقدم لمستويات 4 إلى 7 بحسب المسار.',
    bullet2: 'يمكن أن يشمل التقدم برامج البكالوريوس التكميلية والماجستير وMBA.',
    bullet3: 'أمثلة الجامعات المعروضة مأخوذة من صفحات التقدم المنشورة للجهات المانحة نفسها.',
    bullet4: 'يجب على المتقدمين التأكد من توافر المسار الحالي ومتطلبات القبول قبل التقديم.',
    universitiesLabel: 'أمثلة على الجامعات المذكورة في صفحة التقدم المنشورة',
    bodyPage: 'صفحة الجهة المانحة',
    officialPage: 'صفحة التقدم الرسمية',
    compareTitle: 'استخدم هذه الصفحة كنقطة بداية',
    compareBody: 'لقد لخّصنا هنا معلومات التقدم المنشورة من OTHM وQualifi حتى يتمكن المتعلمون من رؤية المسارات الجامعية الشائعة بسرعة قبل التحدث إلى أحد المستشارين.',
    browseCourses: 'تصفح الدورات',
    speakAdviser: 'تحدث إلى مستشار',
  },
  fr: {
    heroEyebrow: 'Parcours vers les études supérieures',
    heroTitle: 'Progression universitaire',
    heroDescription: 'Découvrez comment les qualifications OTHM et QUALIFI peuvent soutenir votre progression vers des top-up de licence, des masters et des parcours MBA au Royaume-Uni et à l\'international.',
    noticeTitle: 'Note importante sur l\'admission',
    noticeBody: 'L\'admission universitaire reste soumise à l\'évaluation propre de l\'établissement, aux exigences en anglais et aux critères spécifiques du programme.',
    stepsEyebrow: 'Fonctionnement',
    stepsTitle: 'Comment la progression fonctionne généralement',
    steps: [
      'Terminez le diplôme ou le niveau de qualification concerné.',
      'Associez votre qualification à un top-up universitaire ou à une voie de troisième cycle.',
      'Répondez à toute exigence supplémentaire d\'admission, de langue ou de programme fixée par l\'université.',
    ],
    othmEyebrow: 'Qualifications OTHM',
    othmTitle: 'Parcours de progression OTHM',
    othmSummary1: 'OTHM indique que l\'obtention de ses qualifications peut répondre aux conditions académiques standard d\'entrée, sous réserve d\'une évaluation individuelle par l\'université.',
    othmSummary2: 'Ses parcours publiés mettent en avant des top-up de licence, des programmes de master et des voies MBA dans des universités au Royaume-Uni et à l\'étranger.',
    qualifiEyebrow: 'Qualifications QUALIFI',
    qualifiTitle: 'Parcours de progression QUALIFI',
    qualifiSummary1: 'Qualifi indique que ses apprenants progressent vers des diplômes universitaires par admission directe ou avancée au Royaume-Uni et à l\'international.',
    qualifiSummary2: 'Sa page publiée présente des universités où les diplômés Qualifi ont accédé à des parcours de premier cycle et de troisième cycle.',
    bullet1: 'Une admission avec dispense partielle peut être possible pour les niveaux 4 à 7 selon le parcours.',
    bullet2: 'La progression peut inclure des top-up de licence, des masters et des MBA.',
    bullet3: 'Les exemples d\'universités ci-dessous proviennent des pages de progression publiées par les organismes certificateurs.',
    bullet4: 'Les candidats doivent confirmer la disponibilité et les critères d\'entrée avant toute candidature.',
    universitiesLabel: 'Exemples d\'universités figurant sur la page de progression publiée',
    bodyPage: 'Page de l\'organisme certificateur',
    officialPage: 'Page officielle de progression',
    compareTitle: 'Utilisez cette page comme point de départ',
    compareBody: 'Nous avons résumé ici les informations publiées par OTHM et Qualifi afin que les apprenants puissent voir rapidement les parcours universitaires courants avant de parler à un conseiller.',
    browseCourses: 'Parcourir les cours',
    speakAdviser: 'Parler à un conseiller',
  },
};

function ProgressionCard({ accent, bodyCode, summary1, summary2, bullets, universities, bodyHref, officialHref, label, copy }) {
  return (
    <section className="bg-white border border-gray-100 rounded-3xl shadow-sm overflow-hidden">
      <div className={`bg-gradient-to-r ${accent} px-6 py-6 text-white`}>
        <p className="text-xs uppercase tracking-[0.25em] text-white/70 mb-2">{label}</p>
        <h2 className="text-2xl lg:text-3xl font-bold mb-2">{bodyCode}</h2>
        <p className="text-sm text-white/80 max-w-3xl">{summary1}</p>
      </div>

      <div className="p-6 lg:p-8 space-y-6">
        <p className="text-sm text-gray-600 leading-relaxed">{summary2}</p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {bullets.map((item) => (
            <div key={item} className="flex items-start gap-3 rounded-2xl bg-gray-50 border border-gray-100 p-4">
              <CheckCircle size={18} className="text-green-600 mt-0.5 shrink-0" />
              <p className="text-sm text-gray-700 leading-relaxed">{item}</p>
            </div>
          ))}
        </div>

        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400 mb-3">{copy.universitiesLabel}</p>
          <div className="flex flex-wrap gap-2">
            {universities.map((name) => (
              <span key={name} className="rounded-full bg-white border border-gray-200 px-3 py-1.5 text-xs font-medium text-gray-700">
                {name}
              </span>
            ))}
          </div>
        </div>

        <div className="flex flex-wrap gap-3">
          <Link
            to={bodyHref}
            className="inline-flex items-center gap-2 rounded-lg bg-[#0f2b4a] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#2e2a74] transition-colors"
          >
            {copy.bodyPage} <ArrowRight size={15} />
          </Link>
          <a
            href={officialHref}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 rounded-lg border border-gray-200 px-4 py-2.5 text-sm font-semibold text-gray-700 hover:bg-gray-50 transition-colors"
          >
            {copy.officialPage} <ExternalLink size={15} />
          </a>
        </div>
      </div>
    </section>
  );
}

export default function UniversityProgression() {
  const { language } = useSiteLanguage();
  const copy = CONTENT[language] || CONTENT.en;
  const sharedBullets = [copy.bullet1, copy.bullet2, copy.bullet3, copy.bullet4];

  return (
    <div className="min-h-screen bg-gray-50">
      <Seo
        title="University Progression"
        description="Use your OTHM or QUALIFI diploma to progress to a UK university top-up degree or Master's programme. See your progression routes."
        path="/university-progression"
      />
      <section className="bg-gradient-to-br from-[#0f2b4a] via-[#243b86] to-[#2563eb] text-white py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4">
          <p className="text-blue-200 text-sm font-semibold uppercase tracking-[0.3em] mb-3">{copy.heroEyebrow}</p>
          <h1 className="text-4xl lg:text-6xl font-extrabold tracking-tight max-w-4xl mb-4">{copy.heroTitle}</h1>
          <p className="text-base lg:text-lg text-blue-100/85 leading-relaxed max-w-3xl">{copy.heroDescription}</p>
          <div className="flex flex-wrap gap-3 mt-7">
            <span className="rounded-full bg-white/10 border border-white/15 px-4 py-2 text-sm font-semibold">OTHM</span>
            <span className="rounded-full bg-white/10 border border-white/15 px-4 py-2 text-sm font-semibold">QUALIFI</span>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 py-8 lg:py-10">
        <div className="rounded-3xl border border-blue-100 bg-blue-50 p-6 lg:p-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-2xl bg-blue-600 text-white flex items-center justify-center shrink-0">
              <GraduationCap size={22} />
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-900 mb-2">{copy.noticeTitle}</h2>
              <p className="text-sm text-gray-600 leading-relaxed">{copy.noticeBody}</p>
            </div>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 pb-8">
        <div className="bg-white border border-gray-100 rounded-3xl p-6 lg:p-8 shadow-sm">
          <p className="text-blue-600 text-sm font-semibold uppercase tracking-[0.25em] mb-2">{copy.stepsEyebrow}</p>
          <h2 className="text-2xl font-bold text-gray-900 mb-5">{copy.stepsTitle}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {copy.steps.map((step, index) => (
              <div key={step} className="rounded-2xl bg-gray-50 border border-gray-100 p-5">
                <div className="w-8 h-8 rounded-full bg-[#0f2b4a] text-white text-sm font-bold flex items-center justify-center mb-3">
                  {index + 1}
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{step}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 pb-16 space-y-8">
        <div className="grid grid-cols-1 gap-8">
          <ProgressionCard
            accent="from-[#0f2b4a] to-[#312e81]"
            bodyCode={copy.othmTitle}
            summary1={copy.othmSummary1}
            summary2={copy.othmSummary2}
            bullets={sharedBullets}
            universities={OTHM_UNIVERSITIES}
            bodyHref="/awarding-bodies/othm"
            officialHref="https://othm.org.uk/university-progression"
            label={copy.othmEyebrow}
            copy={copy}
          />

          <ProgressionCard
            accent="from-[#1d4ed8] to-[#2563eb]"
            bodyCode={copy.qualifiTitle}
            summary1={copy.qualifiSummary1}
            summary2={copy.qualifiSummary2}
            bullets={sharedBullets}
            universities={QUALIFI_UNIVERSITIES}
            bodyHref="/awarding-bodies/qualifi"
            officialHref="https://qualifi.net/university-progression/"
            label={copy.qualifiEyebrow}
            copy={copy}
          />
        </div>

        <section className="rounded-3xl bg-gradient-to-br from-gray-900 to-[#0f2b4a] p-8 lg:p-10 text-white">
          <div className="max-w-3xl">
            <p className="text-blue-300 text-sm font-semibold uppercase tracking-[0.25em] mb-2">{copy.compareTitle}</p>
            <h2 className="text-2xl lg:text-3xl font-bold mb-3">{copy.heroTitle}</h2>
            <p className="text-sm lg:text-base text-gray-300 leading-relaxed mb-6">{copy.compareBody}</p>
            <div className="flex flex-wrap gap-3">
              <Link
                to="/courses"
                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-3 text-sm font-semibold text-white hover:bg-blue-500 transition-colors"
              >
                {copy.browseCourses} <ArrowRight size={15} />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 rounded-lg border border-white/20 px-5 py-3 text-sm font-semibold text-white hover:bg-white/10 transition-colors"
              >
                {copy.speakAdviser} <Building2 size={15} />
              </Link>
            </div>
          </div>
        </section>
      </section>
    </div>
  );
}